import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlinePauseCircle,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark
} from 'react-icons/hi2';
import { authService } from '../../../services';
import masterDataService from '../../../services/masterDataService';
import SharedModal from '../../../components/common/SharedModal';
import PageLoading from "../../../components/common/PageLoading";
import "../PlannedMaintenanceMasters.css";

const MaintenanceWindowsPage = () => {
  const [windows, setWindows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingWindow, setEditingWindow] = useState(null);
  const [formData, setFormData] = useState({
    windowCode: '',
    windowName: '',
    startTime: '',
    endTime: '',
    isShutdownWindow: false,
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      loadWindows();
    } else {
      setError('Please login to view maintenance windows');
      setLoading(false);
    }
  }, [tenantId]);

  const loadWindows = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await masterDataService.getMaintenanceWindows(tenantId);
      setWindows(data || []);
    } catch (err) {
      setError('Failed to load maintenance windows. Please try again.');
      console.error('Error loading maintenance windows:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredWindows = windows.filter((window) =>
    window.windowCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    window.windowName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingWindow(null);
    setFormData({
      windowCode: '',
      windowName: '',
      startTime: '',
      endTime: '',
      isShutdownWindow: false,
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (window) => {
    setEditingWindow(window);
    // Format time for input (HH:mm)
    const formatTime = (timeStr) => {
      if (!timeStr) return '';
      const date = new Date(`2000-01-01T${timeStr}`);
      if (isNaN(date.getTime())) return '';
      return date.toTimeString().slice(0, 5);
    };
    setFormData({
      windowCode: window.windowCode || '',
      windowName: window.windowName || '',
      startTime: formatTime(window.startTime),
      endTime: formatTime(window.endTime),
      isShutdownWindow: window.isShutdownWindow || false,
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      await masterDataService.deleteMaintenanceWindow(id);
      await loadWindows();
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete maintenance window. Please try again.');
      console.error('Error deleting maintenance window:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.windowCode.trim()) {
      errors.windowCode = 'Window code is required';
    }
    if (!formData.windowName.trim()) {
      errors.windowName = 'Window name is required';
    }
    if (!formData.startTime) {
      errors.startTime = 'Start time is required';
    }
    if (!formData.endTime) {
      errors.endTime = 'End time is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const windowData = {
        tenantId: tenantId,
        windowCode: formData.windowCode.trim(),
        windowName: formData.windowName.trim(),
        startTime: formData.startTime,
        endTime: formData.endTime,
        isShutdownWindow: formData.isShutdownWindow,
      };

      if (editingWindow) {
        windowData.windowId = editingWindow.windowId;
        await masterDataService.updateMaintenanceWindow(editingWindow.windowId, windowData);
      } else {
        await masterDataService.createMaintenanceWindow(windowData);
      }

      await loadWindows();
      setShowModal(false);
      setFormData({ windowCode: '', windowName: '', startTime: '', endTime: '', isShutdownWindow: false });
      setEditingWindow(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.errors || 
                          err.message || 
                          'Failed to save maintenance window. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingWindow(null);
    setFormData({ windowCode: '', windowName: '', startTime: '', endTime: '', isShutdownWindow: false });
    setFormErrors({});
  };

  if (loading) {
    return (
      <div className="pm-masters-page">
        <PageLoading message="Loading maintenance windows..." />
      </div>
    );
  }

  return (
    <div className="pagecontainer pm-masters-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Maintenance Windows</h1>
          <p>Manage maintenance windows for Planned Maintenance</p>
        </div>
        <button className="btn-primary" onClick={handleAdd}>
          <HiOutlinePlus />
          Add Window
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search windows..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="table-info">
            {filteredWindows.length} {filteredWindows.length === 1 ? 'window' : 'windows'}
          </div>
        </div>

        {filteredWindows.length === 0 ? (
          <div className="empty-state">
            <HiOutlinePauseCircle className="empty-icon" />
            <h3>No maintenance windows found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Get started by creating your first maintenance window'}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                Add Window
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Code</th>
                <th>Name</th>
                <th>Start Time</th>
                <th>End Time</th>
                <th>Shutdown Window</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredWindows.map((window) => (
                <tr key={window.windowId}>
                  <td>{window.windowId}</td>
                  <td>{window.windowCode}</td>
                  <td className="name-cell">{window.windowName}</td>
                  <td>{window.startTime || '-'}</td>
                  <td>{window.endTime || '-'}</td>
                  <td>{window.isShutdownWindow ? 'Yes' : 'No'}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(window)}
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                      <button
                        className="btn-icon-delete"
                        onClick={() => setDeleteConfirm(window)}
                        title="Delete"
                      >
                        <HiOutlineTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Modal */}
      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingWindow ? 'Edit Maintenance Window' : 'Add New Maintenance Window'}
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="maintenance-window-form"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting
                ? editingWindow
                  ? 'Updating...'
                  : 'Creating...'
                : editingWindow
                ? 'Update'
                : 'Create'}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="maintenance-window-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="windowCode">
                Window Code <span className="required">*</span>
              </label>
              <input
                type="text"
                id="windowCode"
                value={formData.windowCode}
                onChange={(e) =>
                  setFormData({ ...formData, windowCode: e.target.value })
                }
                className={formErrors.windowCode ? 'error' : ''}
                placeholder="Enter window code"
              />
              {formErrors.windowCode && (
                <span className="field-error">{formErrors.windowCode}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="windowName">
                Window Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="windowName"
                value={formData.windowName}
                onChange={(e) =>
                  setFormData({ ...formData, windowName: e.target.value })
                }
                className={formErrors.windowName ? 'error' : ''}
                placeholder="Enter window name"
              />
              {formErrors.windowName && (
                <span className="field-error">{formErrors.windowName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="startTime">
                Start Time <span className="required">*</span>
              </label>
              <input
                type="time"
                id="startTime"
                value={formData.startTime}
                onChange={(e) =>
                  setFormData({ ...formData, startTime: e.target.value })
                }
                className={formErrors.startTime ? 'error' : ''}
              />
              {formErrors.startTime && (
                <span className="field-error">{formErrors.startTime}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="endTime">
                End Time <span className="required">*</span>
              </label>
              <input
                type="time"
                id="endTime"
                value={formData.endTime}
                onChange={(e) =>
                  setFormData({ ...formData, endTime: e.target.value })
                }
                className={formErrors.endTime ? 'error' : ''}
              />
              {formErrors.endTime && (
                <span className="field-error">{formErrors.endTime}</span>
              )}
            </div>

            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              backgroundColor: '#f8fafc',
              padding: '12px 16px',
              borderRadius: '12px',
              border: '1px solid #e2e8f0; border-color: var(--border-primary)',
              marginTop: '1rem'
            }}>
              <span style={{ 
                fontWeight: 700, 
                color: '#475569', 
                fontSize: '12px', 
                letterSpacing: '0.5px',
                textTransform: 'uppercase'
              }}>
                Shutdown Window
              </span>
              <label className="switch-pm">
                <input
                  type="checkbox"
                  checked={formData.isShutdownWindow}
                  onChange={(e) =>
                    setFormData({ ...formData, isShutdownWindow: e.target.checked })
                  }
                />
                <span className="slider-pm"></span>
              </label>
            </div>
          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={() => handleDelete(deleteConfirm.windowId)}
            >
              Delete
            </button>
          </>
        }
        customWidth="400px"
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete{' '}
            <strong>{deleteConfirm?.windowName}</strong>?
          </p>
          <p className="warning-text" style={{ color: 'var(--status-error)', marginTop: '0.5rem' }}>
            This action cannot be undone.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default MaintenanceWindowsPage;
